import { Input as InputAnt, type InputProps } from 'antd';
import { type ChangeEvent, type ComponentType, useCallback, useMemo } from 'react';

import './TextInput.scss';

import { DEFAULT_FIELD_STYLES } from 'src/components/base';
import { NumberInput } from 'src/components/base/controls/Input/NumberInput';
import { PhoneInput } from 'src/components/base/controls/Input/PhoneInput/PhoneInput';
import { ZipInput } from 'src/components/base/controls/Input/ZipInput';
import { cNs } from 'src/helpers';
import type { IControl } from 'src/types';

export type TextInputType = 'input' | 'password' | 'phone' | 'number' | 'digit' | 'zip';

// biome-ignore lint/suspicious/noExplicitAny: false
const mapInputTypeToComponent: Record<TextInputType, ComponentType<any>> = {
  password: InputAnt.Password,
  input: InputAnt,
  number: NumberInput,
  phone: PhoneInput,
  digit: InputAnt,
  zip: ZipInput,
};

const mapInputTypeToType: Record<TextInputType, string> = {
  password: 'password',
  input: 'input',
  number: 'input',
  phone: 'phone',
  digit: 'input',
  zip: 'input',
};

export type TextInputProps = IControl<string> &
  Omit<InputProps, 'onChange'> & {
    value?: string;
    description?: string | null;
    type?: TextInputType;

    // Optional param for zip input
    country?: string;

    // Optional param for zip input
    showControlIcons?: boolean;
  };

export function TextInput(props: TextInputProps) {
  const {
    onChange,
    type = 'input',
    prefix,
    label,
    description,
    controlClassName,
    controlStyle,
    className: _className,
    ...rest
  } = props;

  const onChangeInput = useCallback((event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value), [onChange]);

  const Component = useMemo(() => mapInputTypeToComponent[type], [type]);

  return (
    <div className={cNs('text-input__container', controlClassName)} style={controlStyle}>
      {label ? typeof label !== 'object' ? <div className="text-input__label">{label}</div> : label : null}
      {description ? <div className="text-input__description">{description}</div> : null}
      <Component
        {...rest}
        prefix={prefix || <span />}
        onChange={onChangeInput}
        type={mapInputTypeToType[type]}
        style={DEFAULT_FIELD_STYLES}
        styles={{
          input: {
            padding: '8px 16px',
          },
        }}
      />
    </div>
  );
}
